'use client';

/**
 * Shared hook for the Plan Commercial Position cards — calls the
 * `plan_commercial_position` strategy once; every card in the
 * mode1_plan_commercial preset calls this same hook, and React Query
 * dedupes so N cards = 1 BFF call (composite-payload pattern, klyde-cards
 * § data shape — same convention as analytics/use-coverage-view.ts).
 *
 * `queryKey` is surfaced on the return so cards pass it straight to
 * `useCardExport({query_key})` — this hook is the single source of
 * truth for the key; cards never reconstruct it.
 *
 * @see klyde_edge/klyde_intelligence/projects/plan_story.py
 */

import { useQuery } from '@tanstack/react-query';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { api } from '@/lib/api/client';

export interface PlanCommercialProductRow { product_name: string; quantity: number; total: number; vendors: string[] }
export interface PlanCommercialVendorRow { vendor: string; total: number; product_count: number; share_pct: number | null }
export interface PlanCommercialData {
  basis: string;
  decided_line_count: number;
  plan_total_decided: number;
  products: PlanCommercialProductRow[];
  vendors: PlanCommercialVendorRow[];
  narrative: string;
}

export function usePlanCommercialView() {
  const { mcpId } = useViewportSelection();
  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'plan_commercial_position'));
  const params = { semantic_id: mcpId ?? '' };
  const queryKey = ['strategy-data', strategy, params] as const;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.getStrategyData<PlanCommercialData>(strategy!, params);
      if (!res.success) throw new Error(res.error || `${strategy} failed`);
      return res.data;
    },
    enabled: !!strategy && !!mcpId,
  });
  return { ...query, queryKey, mcpId, strategy };
}

/** Loading sentinel for KPI tiles (paintedKpi treats '…' as loading). */
export const PLAN_KPI_LOADING = '…';
